import { getMidMonthDate, getMonthAndYear, getSortedTypeIds } from "../utils";
import { BoolResponce } from "../types";
import BackendConnect from "./connect";
import { defaultCurrencyId, defaultSalePlan } from "../constants";
import { StatsFilters } from "../forms/filters/stats";
import { RatingFilters, RatingTableData } from "../forms/filters/rating";

export interface ReadStatsData {
    tab_id: number;
    item_id: number;
    user_id: number;
    date_read?: number;
  }  

export interface EventCounts {
    eventsByType: {
        type_id: number;
        count: number;
    }[];
    totalCount: number;
}

export interface SaleCounts {
    currency_id: number;
    total_amount: number;
}

export interface Dates {
  month: number; 
  year: number; 
}

export interface SalePlanData {
    month: number;
    value: number
}

export interface HireStats {
  employ: number;
  unemploy: number;
  updated: number;
}

export interface RoleCountItem { 
  role_id: number;
  role_name: string; 
  count: number;
}

export interface SaleFunnelDataRow {
  registration: number;
  consultation: number;
  practice: number;
  invoice_question: number;
  account_opening: number;
  subsequent_deposit: number;
}

export interface PracticeFunnelDataRow {
  practice_1: number;
  practice_2: number;
  practice_3: number;
  practice_4: number;
  practice_5: number;
  practice_6: number;
  practice_7: number;
  practice_8: number;
  practice_9: number;
  practice_10: number;
}

export interface GroupSalePlanSetupData {
  group_id: number;
  currency_id: number;
  value: number;
  month: number;
  year: number;
}

export interface GroupSalesArrayData { 
  group_id: number, 
  plan_value: number, 
  actual_value: string 
}

export class BackendStatsService extends BackendConnect {
  protected static instance: BackendStatsService;

  constructor(headers: Record<string, string>) {
    super(headers);
  }

  static getInstance(): BackendStatsService {
    if (!BackendStatsService.instance) {
        BackendStatsService.instance = new BackendStatsService({});
      const bearerToken = localStorage.getItem("bearerToken");
      if (bearerToken) {
        BackendStatsService.instance.setAccessToken(bearerToken);
      }
    }
    return BackendStatsService.instance;
  }

  public async hasUnreadComments (data: ReadStatsData): Promise<{ has: boolean }> {
    const { tab_id, item_id, user_id } = data;
    return this.get(`/stats/uread/${tab_id}/${item_id}/${user_id}`);
  }

  public async markPageRead (data: {
    tab_id: number;
    item_id: number;
    user_id: number;
  }): Promise<BoolResponce> {
    const { tab_id, item_id, user_id } = data;
    return this.get(`/stats/read/${tab_id}/${item_id}/${user_id}`);
  }

  public async getCompletedEventStats(group_id?: number): Promise<{ stats: EventCounts}> {
    return this.get("/stats/events")
  }

  public async getCompletedEventStatsGlobal(group_id?: number): Promise<{ stats: EventCounts}> {
    return this.get(`/stats/eventsglobal/${group_id || 0}`)
  }


  public async getLastSaleAmount(): Promise<{ stats: SaleCounts[]}> {
    return this.get("/stats/sales")
  }

  public async getUserSaleAmount(user_id: number, dates: Dates): Promise<{ stats: SaleCounts[]}> {
    return this.get(`/stats/sales/${user_id}`, {date: getMidMonthDate(dates)})
  }

  public async getPersonalSalePlan(dates?: Dates, currency_id = defaultCurrencyId): Promise<{ value: number }> {
    const { month, year } = dates || getMonthAndYear(new Date())
    return this.get(`/stats/saleplanself/${month}/${year}/${currency_id}`)
  }

  public async getPersonalSalePlanYear (year?: number, currency_id = defaultCurrencyId): Promise<{ values: SalePlanData[] }> {
    const requestYear = year || getMonthAndYear(new Date()).year;
    return this.get(`/stats/saleplanyearself/${requestYear}/${currency_id}`)
  }

  public async getUserSalePlan(user_id: number, dates?: Dates, currency_id = defaultCurrencyId): Promise<{ value: number }> {
    const { month, year } = dates || getMonthAndYear(new Date())
    return this.get(`/stats/saleplanuser/${month}/${year}/${currency_id}/${user_id}`)
  }

  public async getUserSalePlanYear (user_id: number, year?: number, currency_id = defaultCurrencyId): Promise<{ values: SalePlanData[] }> {
    const requestYear = year || getMonthAndYear(new Date()).year;
    return this.get(`/stats/saleplanyear/${requestYear}/${currency_id}/${user_id}`)
  }

  public async setupUserSalePlan (user_id: number, dates: Dates, value: number = defaultSalePlan, currency_id = defaultCurrencyId): Promise<{ value: number }> {
    const { month, year } = dates;
    return this.post("/stats/saleplansetup", {
      user_id,
      month, 
      year,
      value,
      currency_id
    }, "POST");
  }

  public async setupGroupSalePlan (data: GroupSalePlanSetupData): Promise<BoolResponce> {
    return this.post("/stats/groupsaleplansetup", {...data}, "POST");
  }

  public async getGroupSalePlan(group_id: number, currency_id = defaultCurrencyId, dates?: Dates): Promise<{ value: number }> {
    const { month, year } = dates || getMonthAndYear(new Date());
    return this.get(`/stats/saleplangroup/${month}/${year}/${currency_id}/${group_id}`);
  }
  // Возвращают плановое и фактическое значение
  public async getAllGroupsSalePlan 
  (currency_id = defaultCurrencyId, dates?: Dates): Promise<{values: GroupSalesArrayData[]}> {
    const { month, year } = dates || getMonthAndYear(new Date());
    return this.get(`/stats/saleplan/allgroups/${month}/${year}/${currency_id}`);
  }

  public async getTotalGroupsSalePlan 
  (currency_id = defaultCurrencyId, dates?: Dates): Promise<{value: { total_actual: number; total_plan: number; }}> {
    const { month, year } = dates || getMonthAndYear(new Date());
    return this.get(`/stats/saleplan/groupstotal/${month}/${year}/${currency_id}`);
  }

  public async getSinglelGroupsSalePlan 
  (group_id: number, currency_id = defaultCurrencyId, dates?: Dates): 
  Promise<{ values: GroupSalesArrayData }> {
    const { month, year } = dates || getMonthAndYear(new Date());
    return this.get(`/stats/saleplangroupsingle/${month}/${year}/${currency_id}/${group_id}`);
  }

  public async getHireStats(filters: StatsFilters): Promise<{ data: HireStats }> {
    return this.get("/stats/hire", {...filters, event_ids: ""});
  }

  public async getRoleCountStats(filters: StatsFilters): Promise<{ data: RoleCountItem[] }> {
    return this.get("/stats/rolecount", {...filters, event_ids: ""});
  }

  public async getFunnelData (filters: StatsFilters): Promise<{ data: Record<string, number> }> {
    return this.get("/stats/funnel/sale", {...filters, event_ids: getSortedTypeIds (filters.event_ids || [])})
  }

  public async getUserRatings (filters: RatingFilters): Promise<RatingTableData[]> {
    return this.get("/stats/rating/user", {...filters})
  }

  public async getUserRatingsCount (filters: RatingFilters): Promise<RatingTableData[]> {
    return this.get("/stats/rating/usercount", {...filters})
  }

  public async getOfficeRatings (filters: RatingFilters): Promise<RatingTableData[]> {
    return this.get("/stats/rating/office", {...filters})
  }

}

export const statsBackend = BackendStatsService.getInstance();
